import React, { useEffect, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Camera, X, AlertTriangle } from "lucide-react";

/**
 * Riconosce i QR prodotti dall'app.
 *
 * I codici contengono l'indirizzo della pagina pubblica. Chi e' collegato
 * viene portato invece sulla scheda interna, dove ci sono documenti, storico
 * e manutenzioni; chi sta facendo una visita resta sulla pagina pubblica,
 * perche' quella interna non la puo' aprire.
 */
const PATTERNS = [
  { re: /attrezzatura\/([A-Za-z0-9_-]+)/i, label: "Mezzo o attrezzatura", tipo: "mezzo",
    to: (id) => `/attrezzature/${id}`, pubblico: (id) => `/attrezzatura/${id}` },
  { re: /cantiere-pubblico\/([A-Za-z0-9_-]+)/i, label: "Cantiere", tipo: "cantiere",
    to: (id) => `/cantieri/${id}`, pubblico: (id) => `/cantiere-pubblico/${id}` },
  { re: /fascicolo\/([A-Za-z0-9_-]+)/i, label: "Tesserino", tipo: "dipendente",
    to: (id) => `/dipendenti?fascicolo=${id}`, pubblico: (id) => `/fascicolo/${id}` },
  { re: /magazzino-pubblico\/([A-Za-z0-9_-]+)/i, label: "Articolo di magazzino", tipo: "dpi",
    to: (id) => `/magazzino?articolo=${id}`,
    pubblico: (id) => `/magazzino-pubblico/${id}` },
  { re: /materiale\/([A-Za-z0-9_-]+)/i, label: "Materiale", tipo: "materiale",
    to: (id) => `/movimenti-materiali?mat=${id}`,
    pubblico: (id) => `/materiale/${id}` },
];

export function parseQrTarget(text, pubblico = false) {
  for (const p of PATTERNS) {
    const m = (text || "").match(p.re);
    if (m) {
      return { label: p.label, id: m[1], tipo: p.tipo,
               path: pubblico ? p.pubblico(m[1]) : p.to(m[1]) };
    }
  }
  return null;
}

export default function QRScanner({ onClose, onScanned, pubblico = false }) {
  const [error, setError] = useState(null);
  const [unknown, setUnknown] = useState(null);

  useEffect(() => {
    let scanner;
    let stopped = false;

    const start = async () => {
      try {
        scanner = new Html5Qrcode("sgm-qr-reader-box");
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 240 },
          (decoded) => {
            if (stopped) return;
            const target = parseQrTarget(decoded, pubblico);
            if (!target) { setUnknown(decoded); return; }
            stopped = true;
            scanner.stop().then(() => onScanned(target)).catch(() => onScanned(target));
          },
          () => {},
        );
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.warn("QR scanner error:", e);
        setError("Fotocamera non disponibile. Verifica di aver dato il permesso al browser e che la pagina sia su https.");
      }
    };
    start();

    return () => {
      stopped = true;
      if (scanner?.isScanning) scanner.stop().catch(() => {});
    };
  }, [onScanned]);

  return (
    <div className="fixed inset-0 bg-slate-900/90 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-4 max-w-md w-full">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold flex items-center gap-2">
            <Camera className="w-4 h-4 text-orange-500" /> Scansiona QR
          </h3>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="qr-scanner-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {error ? (
          <div className="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded flex gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />{error}
          </div>
        ) : (
          <>
            <div id="sgm-qr-reader-box" className="w-full rounded overflow-hidden" />
            {unknown ? (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-3">
                Codice non riconosciuto: non sembra un QR generato da questa applicazione.
                Continua a inquadrare per riprovare.
              </p>
            ) : (
              <p className="text-xs text-slate-500 mt-3 text-center">
                Inquadra il QR di un mezzo, di un tesserino, di un cantiere o di un articolo.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
